import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Criteria } from '../../../models/Criteria';
import { UserDiscoveryResponse } from '../../../models/UserDiscoveryResponse';
import { useMst } from '../../../models/rootModel';
import { useAnalytics } from '../../../utils/analytics';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import { LoaderDiv } from '../../styledComponent/Boxes.style';
import { CriteriaV3AnswerButton } from './CriteriaV3AnswerButton';
import {
  CriteriaV3AnswerButtonsWrapper,
  CriteriaV3FullStaticScreen,
  CriteriaV3FullStaticScreenContentWrapper,
  CriteriaV3Subtitle,
  CriteriaV3Title,
} from './CriteriaV3Step.style';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { useIsMobileWebview } from '../../../hooks/useIsMobileWebview.hook';

interface CriteriaStepV3Props extends BaseStepPropsV2 {
  criteriaType: string;
  backgroundImageUrl: string;
  responseMapping?: {
    [key: string]: string;
  };
  nextStepId?: string;
}

export const CriteriaV3Step = observer((props: CriteriaStepV3Props) => {
  const {
    criteriaType,
    nextStep,
    responseMapping,
    goToNamedStep,
    isActive,
    nextStepId,
    backgroundImageUrl,
    title,
    subtitle,
    stepName,
  } = props;
  const { Criteria, UserDiscoveryResponse } = useMst();
  const analytics = useAnalytics();
  const searchParams = new URLSearchParams(window.location.search);
  const paramKey = `question_${criteriaType}`;
  const answerFromQuery = searchParams.get(paramKey);
  // variable added to avoid "jump" effect when switching between questions and when we know the answer for the question (from query params)
  const [isReady, setIsReady] = useState(!Boolean(answerFromQuery));

  const criteria = isActive && Criteria.criterias.length > 0 ? Criteria.getCriteriasByType(criteriaType) : [];

  const isMobileWebview = useIsMobileWebview();

  const selectAnswer = (selectedAnswer: Criteria) => {
    UserDiscoveryResponse.addUserAnswers(selectedAnswer);

    analytics.track(`set criteriaV2 - ${criteriaType}`, {
      question: props.title,
      selectedAnswerType: selectedAnswer.name,
      selectedAnswer: selectedAnswer.label,
      relatedPlanLabels: [...(selectedAnswer.meta.relatedPlanLabels ?? [])],
      relatedSegments: [...(selectedAnswer.meta.relatedSegments ?? [])],
      stepName,
      segment: selectedAnswer.name,
      isMobileWebview,
    });
  };

  const onAnswerClick = (selectedAnswer: Criteria) => {
    selectAnswer(selectedAnswer);
    getNextStep(selectedAnswer);
  };

  const getNextStep = (answer: UserDiscoveryResponse) => {
    const nextStepFromResponseMapping = (responseMapping && answer && responseMapping[answer.name]) || null;
    const foundStepId = nextStepFromResponseMapping ?? nextStepId;
    foundStepId ? goToNamedStep(foundStepId) : nextStep();
  };

  useEffect(() => {
    if (isActive && isReady) {
      analytics.track(`view criteriaV2 - ${criteriaType}`, {
        question: props.title,
        stepName,
        isMobileWebview
      });

      UserDiscoveryResponse.removeUserAnswers(props.type);

      return window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, criteriaType, isActive, props.title, isReady, stepName, isMobileWebview]);

  useEffect(() => {
    if (!isActive || isReady || criteria.length < 1) {
      return;
    }
    const criterion = criteria.find((criteria) => criteria.name === answerFromQuery);
    if (criterion) {
      selectAnswer(criterion);
      getNextStep({ type: criterion.type, name: criterion.name, meta: criterion.meta });
    }
    setIsReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerFromQuery, isActive, isReady, criteria]);

  if (!isActive || !isReady) {
    return null;
  }

  return (
    <>
      {Criteria.criterias.length > 0 ? (
        <BaseStepV3 {...props}>
          <CriteriaV3FullStaticScreen imgDesktop={backgroundImageUrl}>
            <CriteriaV3FullStaticScreenContentWrapper>
              <AbsoluteHeader withLogoOnMobileDisplay />
              <CriteriaV3Title>{title}</CriteriaV3Title>
              <CriteriaV3Subtitle>{subtitle}</CriteriaV3Subtitle>
              <CriteriaV3AnswerButtonsWrapper>
                {criteria.map((criteria) => (
                  <CriteriaV3AnswerButton
                    criteria={criteria}
                    key={criteria.id}
                    isSecondary={criteria.meta?.type === 'secondary'}
                    onClick={onAnswerClick}
                  />
                ))}
              </CriteriaV3AnswerButtonsWrapper>
            </CriteriaV3FullStaticScreenContentWrapper>
          </CriteriaV3FullStaticScreen>
        </BaseStepV3>
      ) : (
        <LoaderDiv>
          <CircularProgress />
        </LoaderDiv>
      )}
    </>
  );
});
